<template>
  <Listbox
    v-model="selected_item"
    as="div"
    :class="[classes]"
    class="!border-none bg-light-box-background dark:bg-dark-box-background p-0 relative">
    <ListboxLabel
      v-if="label"
      class="block text-base font-medium text-gray-700 dark:text-dark-body-text bg-light-box-background dark:bg-dark-box-background"
      :for="name">
      {{ label }}
    </ListboxLabel>
    <ListboxButton v-if="!forCreateOnly" as="template">
      <div class="flex mt-1 shadow-sm">
        <div class="relative flex items-stretch flex-grow focus-within:z-10">
          <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <Search class="w-5 h-5 text-gray-400" aria-hidden="true" />
          </div>

          <input
            :value="search_term"
            :disabled="disabled"
            autocomplete="off"
            :placeholder="search_results.length ? '' : translatedPlaceholder"
            :class="[
              search_term?.length > 0 ? 'rounded-br-none rounded-tr-none' : '',
              errorMessage
                ? 'border-status-error-red text-status-error-red placeholder-status-error-red focus:outline-none focus:ring-status-error-red focus:border-status-error-red'
                : '',
              disabled ? 'text-common-grey' : '',
              inputClasses,
              isBoxBackground
                ? 'bg-light-box-background dark:bg-dark-box-background'
                : 'bg-light-screen-background dark:bg-dark-screen-background',

              'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light '
            ]"
            class="block w-full pl-10 transition ease-in-out border rounded-lg focus:ring-blue focus:border-blue-light placeholder-common-grey border-light-divider-border dark:border-dark-divider-border duration-side-menu-transition-duration text-light-body-text dark:text-dark-body-text"
            @blur="handleChange"
            @click="search"
            @keydown="
              ($event) => {
                if ($event.keyCode == 32) {
                  $event.stopPropagation()
                }
              }
            "
            @input="
              ($event) => {
                show_results = true
                ;(search_term = $event.target.value), handleChange($event)
              }
            ">
          <div v-show="errorMessage" class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <svg class="w-2 h-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
              <path stroke-linecap="round" stroke-width="1.5" d="M1 1l6 6m0-6L1 7" />
            </svg>
          </div>
          <p v-if="errorMessage && !meta.valid" class="absolute inset-y-0 top-10 right-0 leading-tight pt-[2px] text-[13px] text-red-400">
            {{ errorMessage }}
          </p>
          <p v-else-if="infoMessage" class="absolute inset-y-0 top-10 left-0 leading-tight pt-[2px] text-[13px] text-gray-400">
            {{ infoMessage }}
          </p>
        </div>
        <div v-if="search_results.length" class="absolute inset-y-0 right-8 z-10 flex items-center">
          <div class="flex items-center" @click="show_results = true">
            <div class="flex justify-between gap-1 pr-2">
              <div
                v-for="type in all_types"
                :key="type"
                class="px-1 flex justify-between items-center dark:text-dark-body-text border rounded-full shadow-sm py-0.5 text-left cursor-pointer focus:outline-none sm:text-sm"
                :class="[selected_types.includes(type) ? 'bg-[#0F9BD220] border-[#2DA7D7]' : '']"
                @click="filter(type)">
                <span class="block truncate" :class="[selected_types.includes(type) ? 'text-[#2DA7D7]' : 'text-common-grey']">{{
                  $t(type)
                }}</span>
              </div>
            </div>
          </div>
        </div>
        <button
          v-if="search_results.length && !disabled && !is_searching"
          type="button"
          :class="
            isBoxBackground
              ? 'bg-light-box-background dark:bg-dark-box-background'
              : 'bg-light-screen-background dark:bg-dark-screen-background'
          "
          class="relative inline-flex items-center px-1 py-2 -ml-px space-x-2 text-sm font-medium transition ease-in-out border rounded-tl-none rounded-bl-none border-light-divider-border dark:border-dark-divider-border rounded-r-md focus:outline-none duration-side-menu-transition-duration"
          @click="reset">
          <X class="w-5 h-5 text-gray-400 hover:text-gray-700" aria-hidden="true" />
        </button>

        <button
          v-else-if="is_searching"
          type="button"
          :class="
            isBoxBackground
              ? 'bg-light-box-background dark:bg-dark-box-background'
              : 'bg-light-screen-background dark:bg-dark-screen-background'
          "
          class="relative inline-flex items-center px-1 py-2 -ml-px space-x-2 text-sm font-medium border rounded-tl-none rounded-bl-none rounded-r-md focus:outline-none">
          <RotateCw class="w-5 h-5 text-gray-500 animate-spin" aria-hidden="true" />
        </button>
      </div>
    </ListboxButton>

    <transition
      v-show="(search_term != null && show_results && !is_searching) || forCreateOnly"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0">
      <ListboxOptions
        static
        :class="{ 'top-[42px]': !forCreateOnly }"
        class="bg-light-box-background dark:bg-dark-box-background absolute inset-0 z-10 w-full shadow-lg max-h-60 h-fit rounded-md text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
        <template v-if="!forCreateOnly">
          <ListboxOption v-if="search_results.length == 0 && search_term">
            <div class="flex items-center justify-center pt-2 my-2 text-center text-common-grey">
              {{ $t("No tasks/goals matching search query") }}
            </div>
          </ListboxOption>
          <ListboxOption v-else-if="search_results.length == 0">
            <div class="flex items-center justify-center pt-2 my-2 text-center text-common-grey">
              {{ $t("No Tasks/goals to display") }}
            </div>
          </ListboxOption>
        </template>
        <ListboxOption
          v-show="search_results.length == 0 || forCreateOnly"
          v-slot="{}"
          key="createGroup"
          class="flex items-center justify-center">
          <div
            class="m-2 btn primary-btn"
            @click="
              () => {
                $emitter.emit(ALL_EMITTERS.CREATE_TASK, { inMilestone: true })
                show_results = false
              }
            ">
            <div class="flex flex-nowrap">
              <Plus class="w-5 h-5 mr-2 text-white" />
              {{ $t("Create Task") }}
            </div>
          </div>
          <span>{{ $t("Or") }}</span>
          <div
            class="m-2 btn primary-btn"
            @click="
              () => {
                $emitter.emit(ALL_EMITTERS.CREATE_GOAL, { inMilestone: true })
                show_results = false
              }
            ">
            <div class="flex flex-nowrap">
              <Plus class="w-5 h-5 mr-2 text-white" />
              {{ $t("Create Goal") }}
            </div>
          </div>
        </ListboxOption>
        <template v-if="!forCreateOnly">
          <ListboxOption v-for="item in search_results" v-slot="{}" :key="item" :value="item">
            <div
              :class="''"
              class="relative py-2 pl-8 pr-4 transition ease-in-out rounded-lg cursor-pointer group/name hover:bg-light-brand-blue dark:hover:bg-dark-brand-blue duration-side-menu-transition-duration">
              <span
                :class="['text-light-body-text dark:text-dark-body-text group-hover/name:text-dark-body-text']"
                class="block truncate transition ease-in-out duration-side-menu-transition-duration">
                {{ item[labelAttr] }}
              </span>
              <span
                :class="['text-light-body-text dark:text-dark-body-text group-hover/name:text-dark-body-text']"
                class="absolute inset-y-0 left-0 flex items-center pl-1.5 transition ease-in-out duration-side-menu-transition-duration">
                <Plus class="w-5 h-5" aria-hidden="true" />
              </span>
            </div>
          </ListboxOption>
        </template>
      </ListboxOptions>
    </transition>
  </Listbox>
</template>

<script>
import { Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions } from "@headlessui/vue"
import { Search, X, Plus, RotateCw } from "lucide-vue-next"
import dataService from "@/services/data.service"
import { useField } from "vee-validate"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"

export default {
  components: {
    Search,
    X,
    RotateCw,
    Plus,
    Listbox,
    ListboxLabel,
    ListboxOptions,
    ListboxOption,
    ListboxButton
  },
  props: {
    selectedItems: {
      type: Array,
      required: false
    },
    forCreateOnly: {
      type: Boolean,
      default: false
    },
    clearOnSelect: {
      type: Boolean,
      default: false
    },
    name: {
      type: String,
      required: true
    },
    value: {
      type: String,
      required: false
    },
    label: {
      type: String,
      required: false
    },
    infoMessage: {
      type: String,
      default: ""
    },
    placeholder: {
      type: String,
      required: false,
      default: "Search"
    },
    inputClasses: {
      type: String,
      default: "text"
    },
    classes: {
      type: String,
      default: "text"
    },
    disabled: {
      type: Boolean,
      default: false
    },
    labelAttr: {
      type: String,
      default: "name"
    },
    valueAttr: {
      type: String,
      required: false
    },
    searchField: {
      type: String,
      default: "name"
    },
    filterField: {
      type: String,
      required: false
    },
    filterFieldValue: {
      type: String,
      required: false
    },
    isBoxBackground: {
      type: Boolean,
      default: false
    }
  },
  emits: ["chosen", "changed"],
  setup(props) {
    const {
      errorMessage,

      handleChange,
      setValue,
      meta
    } = useField(props.name, undefined, {
      initialValue: props.value
    })

    return {
      handleChange,

      setValue,
      errorMessage,

      meta
    }
  },

  data() {
    return {
      ALL_EMITTERS,
      all_types: ["Tasks", "Goals"],
      selected_types: [],
      is_searching: false,
      show_results: true,
      search_term: null,
      search_results: [],
      selected_item: null,
      dont_fetch: false
    }
  },
  computed: {
    translatedPlaceholder() {
      return this.$t(this.placeholder)
    }
  },
  watch: {
    search_term() {
      this.$emit("changed", this.search_term)
      if (this.search_term != null && this.show_results) {
        if (!this.dont_fetch) {
          this.$utils.debounce(() => {
            this.search()
          }, 800)
        }
      }
    },
    selected_item() {
      if (this.selected_item) {
        this.$emit("chosen", { ...this.selected_item })
        if (this.clearOnSelect) {
          this.search_term = ""
        } else {
          this.search_term = this.selected_item[this.labelAttr]
        }
        this.search_results = []
        this.show_results = false
        if (this.valueAttr) {
          this.setValue(this.selected_item[this.valueAttr])
        }
      }
    }
  },

  methods: {
    search() {
      this.is_searching = true
      let filter = {}
      if (this.search_term == null) {
        this.dont_fetch = true
        setTimeout(() => {
          this.dont_fetch = false
        }, 500)
        this.search_term = ""
      }
      filter[this.searchField] = this.search_term
      if (this.filterField && this.filterFieldValue) {
        filter[this.filterField] = this.filterFieldValue
      }
      if (this.selected_types.length == 0 || this.selected_types.length == 2) {
        dataService
          .get(`/api/organizations/${this.$store.state.organization.id}/goals/`, filter)
          .then((result) => {
            dataService
              .get(`/api/organizations/${this.$store.state.organization.id}/tasks/`, filter)
              .then((result2) => {
                this.is_searching = false
                this.search_results = result.data.results.concat(result2.data.results)

                this.search_results = this.search_results.filter(
                  (item) => !this.selectedItems.find((s_item) => (s_item.id ? s_item.id == item.id : s_item == item.id))
                )
              })
              .catch(() => {
                this.is_searching = false
              })
          })
          .catch(() => {
            this.is_searching = false
          })
      } else if (this.selected_types[0] == "Goals") {
        dataService
          .get(`/api/organizations/${this.$store.state.organization.id}/goals/`, filter)
          .then((result) => {
            this.is_searching = false
            this.search_results = result.data.results
            this.search_results = this.search_results.filter(
              (item) => !this.selectedItems.find((s_item) => (s_item.id ? s_item.id == item.id : s_item == item.id))
            )
          })
          .catch(() => {
            this.is_searching = false
          })
      } else {
        dataService
          .get(`/api/organizations/${this.$store.state.organization.id}/tasks/`, filter)
          .then((result) => {
            this.is_searching = false
            this.search_results = result.data.results
            this.search_results = this.search_results.filter(
              (item) => !this.selectedItems.find((s_item) => (s_item.id ? s_item.id == item.id : s_item == item.id))
            )
          })
          .catch(() => {
            this.is_searching = false
          })
      }
    },
    reset() {
      this.search_term = null
      this.search_results = []
      this.show_results = false
    },
    filter(type) {
      if (this.selected_types.includes(type)) {
        this.selected_types = this.selected_types.filter((o) => o != type)
      } else {
        this.selected_types.push(type)
      }
      this.search()
    }
  }
}
</script>

<style scoped>
.mh-48 {
  max-height: 10rem;
}
</style>
